import { useState, useCallback, useRef } from 'react';

export interface Reaction {
  emoji: string;
  user: string; // 'falco' or 'rook'
}

const STORAGE_KEY = 'muse-reactions';

function loadFromStorage(): Record<string, Reaction[]> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveToStorage(data: Record<string, Reaction[]>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
}

export type SendReactionFn = (msg: {
  type: 'reaction';
  message_id: string;
  emoji: string;
  action: 'add' | 'remove';
}) => void;

export function useReactions() {
  const [reactions, setReactions] = useState<Record<string, Reaction[]>>(loadFromStorage);

  // Wired externally by the component that owns the WS send function (ChatArea → useMuseChat).
  // Using a ref so toggleReaction stays stable without needing wsRef in its deps.
  const sendReactionRef = useRef<SendReactionFn | null>(null);

  const toggleReaction = useCallback((messageId: string, emoji: string) => {
    setReactions(prev => {
      const msgReactions = prev[messageId] ?? [];
      const existingIdx = msgReactions.findIndex(r => r.emoji === emoji && r.user === 'falco');
      const action: 'add' | 'remove' = existingIdx >= 0 ? 'remove' : 'add';

      let updated: Reaction[];
      if (existingIdx >= 0) {
        updated = msgReactions.filter((_, i) => i !== existingIdx);
      } else {
        updated = [...msgReactions, { emoji, user: 'falco' }];
      }

      const next = { ...prev, [messageId]: updated };
      if (updated.length === 0) delete next[messageId];
      saveToStorage(next);

      // Optimistic local update already applied above — now send to server
      sendReactionRef.current?.({ type: 'reaction', message_id: messageId, emoji, action });

      return next;
    });
  }, []);

  // Called by useChat when a reaction ServerMessage arrives from the gateway.
  const mergeServerReaction = useCallback((
    messageId: string,
    emoji: string,
    user: string,
    action: 'add' | 'remove',
  ) => {
    console.log('[useReactions] mergeServerReaction:', messageId, emoji, user, action);
    setReactions(prev => {
      const msgReactions = prev[messageId] ?? [];
      const existingIdx = msgReactions.findIndex(r => r.emoji === emoji && r.user === user);

      let updated: Reaction[];
      if (action === 'add') {
        if (existingIdx >= 0) return prev; // already present — no-op
        updated = [...msgReactions, { emoji, user }];
      } else {
        if (existingIdx < 0) return prev; // already absent — no-op
        updated = msgReactions.filter((_, i) => i !== existingIdx);
      }

      const next = { ...prev, [messageId]: updated };
      if (updated.length === 0) delete next[messageId];
      saveToStorage(next);
      return next;
    });
  }, []);

  // Called by useChat on reaction_sync — bulk-replaces state from server truth.
  const loadReactions = useCallback((
    serverReactions: Array<{ message_id: string; emoji: string; user: string }>,
  ) => {
    const grouped: Record<string, Reaction[]> = {};
    for (const r of serverReactions) {
      if (!grouped[r.message_id]) grouped[r.message_id] = [];
      grouped[r.message_id].push({ emoji: r.emoji, user: r.user });
    }
    saveToStorage(grouped);
    setReactions(grouped);
  }, []);

  const getReactions = useCallback((messageId: string): Reaction[] => {
    return reactions[messageId] ?? [];
  }, [reactions]);

  return {
    reactions,
    toggleReaction,
    getReactions,
    mergeServerReaction,
    loadReactions,
    sendReactionRef,
  };
}
